import { ColProps, theme } from 'antd'
import { DebounceSettings, debounce } from 'lodash'
import { useState, useEffect } from 'react'

type ScreenSize = keyof Pick<ColProps, 'xs' | 'sm' | 'md' | 'lg' | 'xl'>

export type WindowSizeHookProps = {
  delay?: number
  delaySetting?: DebounceSettings
  options?: {
    needWindowSize?: boolean
    needSmallSize?: boolean
  }
  smallSize?: number | ScreenSize
}

// Hook
export default function useWindowSize(props?: WindowSizeHookProps) {
  const themeToken = theme.useToken().token
  const { delay, delaySetting, smallSize, options } = props || {
    delay: undefined,
    delaySetting: undefined,
    smallSize: 'xs',
    options: undefined,
  }

  const mapScreenWidth: Record<ScreenSize, number> = {
    xs: themeToken.screenXSMax,
    sm: themeToken.screenSMMax,
    md: themeToken.screenMDMax,
    lg: themeToken.screenLGMax,
    xl: themeToken.screenXLMax,
  }

  const [windowSize, setWindowSize] = useState<{
    width: number | undefined
    height: number | undefined
  }>({
    width: undefined,
    height: undefined,
  })
  const [isSmallSize, setIsSmallSize] = useState(false)

  const { needWindowSize, needSmallSize } = options || {
    needWindowSize: true,
    needSmallSize: true,
  }

  const handleGetIsSmallSize = () => {
    if (
      !!window.innerWidth &&
      window.innerWidth <=
        (typeof smallSize === 'string'
          ? mapScreenWidth[smallSize]
          : smallSize || mapScreenWidth.sm)
    ) {
      setIsSmallSize(true)
    } else {
      setIsSmallSize(false)
    }
  }

  useEffect(() => {
    const handleUpdate = () => {
      if (needWindowSize !== undefined ? needWindowSize : true) {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        })
      }

      if (needSmallSize !== undefined ? needSmallSize : true) {
        handleGetIsSmallSize()
      }
    }

    const handleDebounceUpdate = debounce(
      () => handleUpdate(),
      delay,
      delaySetting
    )

    const handleResize = () => {
      if (delay) {
        handleDebounceUpdate()
      } else {
        handleUpdate()
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return { windowSize, isSmallSize }
}
